import { createContext, useContext } from 'react';

import { differenceInCalendarDays, format } from 'date-fns';

import Segment from 'helpers/segment';

export const REPORTS_PAGE = {
  ORDERS: 'order reports',
  ITEMS: 'item reports',
};

export const FILTER_TYPE = {
  LOCATION: 'location',
  STATUS: 'status',
  CATEGORY: 'category',
  MENU: 'menu',
  DATE: 'date',
};

export const DATE_SELECTION_TYPE = {
  PRESET: 'preset',
  CUSTOM: 'custom',
  STEPPER: 'stepper',
};

export const TRACKING_SURFACE = {
  LISTING: 'listing',
  ITEM_FLYOUT: 'item flyout',
};

export const rangeLengthDays = range => differenceInCalendarDays(range.to, range.from) + 1;

export const rangeSpan = range => `${format(range.from, 'yyyy-MM-dd')} to ${format(range.to, 'yyyy-MM-dd')}`;

export const presetLabel = preset => preset.toLowerCase().replace(/_/g, ' ');

export const makeReportsTracker = ({ page, venueIds, isMobile }) => {
  const event = (name, properties = {}) =>
    Segment.event(name, {
      page,
      'venue ids': venueIds,
      'venue count': venueIds.length,
      'is multi venue': venueIds.length > 1,
      'is mobile': isMobile,
      ...properties,
    });

  return {
    pageViewed: () => event('Reports - Page Viewed'),
    filterOpened: filterType => event('Reports - Filter Opened', { 'filter type': filterType }),
    filterApplied: (filterType, properties = {}) =>
      event('Reports - Filter Applied', { 'filter type': filterType, ...properties }),
    filterRemoved: (filterType, properties = {}) =>
      event('Reports - Filter Removed', { 'filter type': filterType, ...properties }),
    dateRangeSelected: ({ selectionType, range, rangeDays }) =>
      event('Reports - Date Range Selected', { 'selection type': selectionType, range, 'range days': rangeDays }),
    searchApplied: ({ suggestionSelected, resultCount }) =>
      event('Reports - Search Applied', { 'suggestion selected': suggestionSelected, 'result count': resultCount }),
    rowOpened: ({ entryPoint, ...ids }) => event('Reports - Row Opened', { 'entry point': entryPoint, ...ids }),
    breakdownToggled: expanded => event('Reports - Breakdown Toggled', { expanded }),
    tableSorted: ({ column, direction }) => event('Reports - Table Sorted', { column, direction }),
    tablePageChanged: ({ pageNumber, direction }) =>
      event('Reports - Table Page Changed', { 'page number': pageNumber, direction }),
    csvDownloaded: ({ rowsExported, isFiltered, filters = {} }) =>
      event('Reports - CSV Downloaded', { 'rows exported': rowsExported, 'is filtered': isFiltered, ...filters }),
    emptyStateShown: (filters = {}) => event('Reports - Empty State Shown', filters),
    errorShown: surface => event('Reports - Error Shown', { surface }),
    errorRetried: surface => event('Reports - Error Retried', { surface }),
  };
};

const noop = () => {};
const NOOP_TRACKER = Object.fromEntries(
  Object.keys(makeReportsTracker({ page: '', venueIds: [], isMobile: false })).map(key => [key, noop])
);

export const SkuReportsTrackingContext = createContext(NOOP_TRACKER);

export const useReportsTracking = () => useContext(SkuReportsTrackingContext);
